import { useEffect, useRef, useState } from 'react';

import { combine } from '@atlaskit/pragmatic-drag-and-drop/combine';
import {
  draggable,
  dropTargetForElements,
} from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import { attachClosestEdge } from '@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DragHandleIcon from '@mui/icons-material/DragHandle';
import { useMediaQuery, Typography } from '@mui/material';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import { styled, useTheme } from '@mui/material/styles';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

const StyledDraggingListItem = styled(ListItem)(({ theme, isdragging }) => ({
  background: isdragging ? theme.palette.action.hover : '#fff',
  textDecoration: 'none',
  padding: theme.spacing(2),
}));

const StyledWarningChip = styled(Chip)(({ theme }) => ({
  borderColor: `${theme.palette.warning.dark} !important`, // Force the color to apply
  color: `${theme.palette.warning.dark} !important`,
  marginRight: theme.spacing(5),
}));

const StyledWordBreak = styled(Typography)(({ theme }) => ({
  wordBreak: 'break-word',
  [theme.breakpoints.down('sm')]: {
    marginRight: theme.spacing(5),
  },
  [theme.breakpoints.up('sm')]: {
    marginRight: theme.spacing(2),
  },
}));

const DraggableListItem = ({ item, index, onRemoveFromList }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isXsScreen = useMediaQuery(theme.breakpoints.down('sm'));

  const itemRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);

  useEffect(() => {
    const itemEl = itemRef.current;
    if (!itemEl) return;

    return combine(
      draggable({
        element: itemEl,
        getInitialData: () => ({ itemId: item.id, index: index }),
        onDragStart: () => setIsDragging(true),
        onDrop: () => setIsDragging(false),
      }),
      dropTargetForElements({
        element: itemEl,
        getData: ({ input, element }) => {
          // To attach card data to a drop target
          const data = { itemId: item.id, index: index };

          // Attaches the closest edge (top or bottom) to the data object
          // This data will be used to determine where to drop card relative
          // to the target card.
          return attachClosestEdge(data, {
            input,
            element,
            allowedEdges: ['top', 'bottom'],
          });
        },
        getIsSticky: () => true, // To make a drop target "sticky"
      })
    );
  }, []);

  return (
    <StyledDraggingListItem
      ref={itemRef}
      isdragging={isDragging}
      secondaryAction={
        <IconButton
          edge="end"
          title={t('delete', { ns: 'buttons' })}
          aria-label="delete"
          onClick={() => onRemoveFromList(index)}
          size="large"
        >
          <DeleteOutlineIcon />
        </IconButton>
      }
    >
      <ListItemIcon>
        <DragHandleIcon color="action" />
      </ListItemIcon>
      <ListItemText
        primary={
          <StyledWordBreak component="p" data-testid="synonym word">
            {item.value}
          </StyledWordBreak>
        }
      />
      {!isXsScreen && item.notSaved && (
        <StyledWarningChip
          size="small"
          label={t('synonym.notSaved', { ns: 'project' })}
          variant="outlined"
        />
      )}
    </StyledDraggingListItem>
  );
};

DraggableListItem.propTypes = {
  item: PropTypes.shape({
    id: PropTypes.string.isRequired,
    value: PropTypes.string.isRequired,
    notSaved: PropTypes.bool,
  }),
  index: PropTypes.number.isRequired,
  onRemoveFromList: PropTypes.func.isRequired,
};

export default DraggableListItem;
